import { useEffect, useMemo, useRef, useState } from "react";
import { Layer, Rect as KonvaRect, Stage } from "react-konva";

import { useSceneBrandkitFonts } from "../hooks";
import { BlockNode, type KonvaRenderContext } from "../konva/block-node";
import type { SceneBrandkit, ScenePlan, SocialPostGenerationTaskDetail } from "../types";

type ScenePreviewLinkedAsset = Pick<
  SocialPostGenerationTaskDetail["linkedAssets"][number],
  | "generatedAssetId"
  | "blockId"
  | "role"
  | "position"
  | "objectKey"
  | "mimeType"
  | "width"
  | "height"
  | "prompt"
  | "signedUrl"
  | "createdAt"
>;

export function SceneTemplatePreview({
  plan,
  linkedAssets,
  brandkit,
  selectedBlockId = null,
  onSelectBlock = () => {},
  maxWidth = 640,
  showCanvasMeta = true,
}: {
  plan: ScenePlan;
  linkedAssets: ScenePreviewLinkedAsset[];
  brandkit: SceneBrandkit;
  selectedBlockId?: string | null;
  onSelectBlock?: (blockId: string) => void;
  maxWidth?: number;
  showCanvasMeta?: boolean;
}) {
  useSceneBrandkitFonts(brandkit);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const element = containerRef.current;

    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }

    const updateWidth = (nextWidth: number) => {
      setContainerWidth(Math.max(0, Math.floor(nextWidth)));
    };

    updateWidth(element.clientWidth);

    const observer = new ResizeObserver((entries) => {
      updateWidth(entries[0]?.contentRect.width ?? element.clientWidth);
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  const sortedBlocks = useMemo(
    () => plan.scene.blocks.toSorted((a, b) => a.zIndex - b.zIndex),
    [plan.scene.blocks],
  );
  const imageAssetByBlockId = useMemo(
    () => new Map(linkedAssets.map((asset) => [asset.blockId, asset] as const)),
    [linkedAssets],
  );

  const measuredWidth =
    containerWidth > 0 ? Math.max(containerWidth, 220) : plan.scene.canvas.width;
  const previewWidth = Math.min(measuredWidth, maxWidth);
  const scale = previewWidth / plan.scene.canvas.width;
  const previewHeight = Math.round(plan.scene.canvas.height * scale);

  const renderContext: KonvaRenderContext = {
    scale,
    selectedBlockId,
    imageAssetByBlockId,
    brandkit,
    onSelectBlock,
  };

  return (
    <div ref={containerRef} className="space-y-3">
      {showCanvasMeta ? (
        <div className="text-xs text-muted-foreground">
          {plan.scene.canvas.width} x {plan.scene.canvas.height}
        </div>
      ) : null}
      <div className="overflow-auto">
        <div className="mx-auto" style={{ width: previewWidth, height: previewHeight }}>
          <Stage width={previewWidth} height={previewHeight}>
            <Layer>
              <KonvaRect
                x={0}
                y={0}
                width={previewWidth}
                height={previewHeight}
                fill={plan.scene.canvas.backgroundFill}
              />
              {sortedBlocks.map((block) => (
                <BlockNode key={block.id} block={block} context={renderContext} />
              ))}
            </Layer>
          </Stage>
        </div>
      </div>
    </div>
  );
}
